import Debug from '../utils/Debug.js';

/**
 * CharacterMetadata - Loads and parses character metadata.json files
 *
 * Provides access to:
 * - Character info (id, name, size)
 * - Static rotation frames (8 directions)
 * - Animation frames (animation name -> direction -> frame paths)
 */
export default class CharacterMetadata {
  /**
   * Create a new CharacterMetadata instance
   * @param {string} basePath - Base path to character folder (e.g., '/characters/gandalf')
   * @param {Object} data - Parsed metadata.json data
   */
  constructor(basePath, data) {
    this.basePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
    this.data = data;

    // Character info
    this.id = data.character.id;
    this.name = data.character.name;
    this.spriteWidth = data.character.size.width;
    this.spriteHeight = data.character.size.height;
    this.directions = data.character.directions;
    this.view = data.character.view;

    // Frames
    this.rotations = data.frames.rotations || {};
    this.animations = data.frames.animations || {};

    // Available animation names
    this.animationNames = Object.keys(this.animations);
  }

  /**
   * Load a character's metadata from a path
   * @param {string} basePath - Base path to character folder
   * @returns {Promise<CharacterMetadata>} Loaded metadata
   */
  static async load(basePath) {
    const metadataUrl = `${basePath}/metadata.json`;

    try {
      const response = await fetch(metadataUrl);
      if (!response.ok) {
        throw new Error(`Failed to load metadata: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return new CharacterMetadata(basePath, data);
    } catch (error) {
      Debug.error('CharacterMetadata', `Error loading character metadata from ${basePath}:`, error);
      throw error;
    }
  }

  /**
   * Get the static rotation frame for a direction
   * @param {string} direction - Direction name (e.g., 'south', 'north-east')
   * @returns {string|null} Full path to frame, or null if not found
   */
  getRotationFrame(direction) {
    const relativePath = this.rotations[direction];
    if (!relativePath) return null;
    return `${this.basePath}/${relativePath}`;
  }

  /**
   * Get animation frames for a specific animation and direction
   * @param {string} animationName - Animation name (e.g., 'walk', 'idle')
   * @param {string} direction - Direction name
   * @returns {string[]|null} Array of full paths to frames, or null if not found
   */
  getAnimationFrames(animationName, direction) {
    const animation = this.animations[animationName];
    if (!animation) return null;

    const frames = animation[direction];
    if (!frames) return null;

    return frames.map(relativePath => `${this.basePath}/${relativePath}`);
  }

  /**
   * Get a specific frame from an animation
   * @param {string} animationName - Animation name
   * @param {string} direction - Direction name
   * @param {number} frameIndex - Frame index
   * @returns {string|null} Full path to frame, or null if not found
   */
  getAnimationFrame(animationName, direction, frameIndex) {
    const frames = this.getAnimationFrames(animationName, direction);
    if (!frames || frameIndex < 0 || frameIndex >= frames.length) return null;
    return frames[frameIndex];
  }

  /**
   * Get the frame count for an animation in a specific direction
   * @param {string} animationName - Animation name
   * @param {string} direction - Direction name
   * @returns {number} Number of frames, or 0 if not found
   */
  getFrameCount(animationName, direction) {
    const frames = this.getAnimationFrames(animationName, direction);
    return frames ? frames.length : 0;
  }

  /**
   * Check if an animation exists
   * @param {string} animationName - Animation name to check
   * @returns {boolean} True if animation exists
   */
  hasAnimation(animationName) {
    return animationName in this.animations;
  }

  /**
   * Get all available directions for an animation
   * @param {string} animationName - Animation name
   * @returns {string[]} Array of direction names
   */
  getAnimationDirections(animationName) {
    const animation = this.animations[animationName];
    if (!animation) return [];
    return Object.keys(animation);
  }

  /**
   * Preload all frames for faster display
   * @returns {Promise<void>} Resolves when all images are loaded
   */
  async preloadFrames() {
    const allPaths = new Set();

    // Add rotation frames
    for (const direction of Object.keys(this.rotations)) {
      const path = this.getRotationFrame(direction);
      if (path) allPaths.add(path);
    }

    // Add animation frames
    for (const animName of this.animationNames) {
      const directions = this.getAnimationDirections(animName);
      for (const direction of directions) {
        const frames = this.getAnimationFrames(animName, direction);
        if (frames) {
          frames.forEach(path => allPaths.add(path));
        }
      }
    }

    // Load all images
    const loadPromises = Array.from(allPaths).map(path => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(path);
        img.onerror = () => {
          Debug.warn('CharacterMetadata', `Failed to preload: ${path}`);
          resolve(path); // Resolve anyway to not block other loads
        };
        img.src = path;
      });
    });

    await Promise.all(loadPromises);
    Debug.log('CharacterMetadata', `Preloaded ${allPaths.size} character frames`);
  }

  /**
   * Get metadata summary
   * @returns {Object} Summary object
   */
  getSummary() {
    return {
      id: this.id,
      name: this.name,
      size: { width: this.spriteWidth, height: this.spriteHeight },
      directions: this.directions,
      animations: this.animationNames,
      frameCount: this.animationNames.reduce((total, name) => {
        const dirs = this.getAnimationDirections(name);
        return total + dirs.reduce((t, d) => t + this.getFrameCount(name, d), 0);
      }, 0)
    };
  }
}
